/**
 * Cache-busting voor de (als bookmark/PWA geïnstalleerde) app.
 *
 * Bij elke build bakt Vite een uniek versienummer in de bundle
 * (__APP_VERSION__) en schrijft hetzelfde nummer naar /version.json.
 * Tijdens runtime vergelijken we die twee periodiek; wijkt de server af,
 * dan staat er een nieuwe build live en herladen we op het eerst
 * veilige moment:
 *
 *  - zodra de app weer zichtbaar wordt (monteur opent 's ochtends de
 *    bookmark → iOS/Android herstelt de bevroren pagina uit geheugen);
 *  - bij de eerstvolgende routewissel (harde navigatie i.p.v. SPA-swap);
 *  - als het laden van een lazy chunk faalt (oude index.html verwijst
 *    naar verwijderde gehashte bestanden).
 */

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_GUARD_KEY = 'app-version-reloaded-at';

const currentVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null;

let updateAvailable = false;
let checking = false;

async function checkForUpdate() {
  if (checking || updateAvailable || !currentVersion) return false;
  checking = true;
  try {
    // Query-param + no-store zodat geen enkele HTTP-cache (browser, CDN,
    // iOS standalone webview) een oud antwoord kan teruggeven.
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return false;
    const { version } = await response.json();
    if (version && version !== currentVersion) {
      updateAvailable = true;
    }
  } catch {
    // Offline of version.json (nog) niet aanwezig — gewoon later opnieuw.
  } finally {
    checking = false;
  }
  return updateAvailable;
}

/**
 * Herlaad hooguit één keer per minuut, zodat een eventueel
 * misconfigureerde cache nooit een reload-loop kan veroorzaken.
 */
function safeReload() {
  const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
  if (Date.now() - last < 60 * 1000) return;
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  window.location.reload();
}

/**
 * Start de versiebewaking. Aanroepen vanuit main.js (alleen in productie).
 * @param {import('vue-router').Router} router
 */
export function initAppVersionCheck(router) {
  if (!currentVersion) return;

  // 0. De app gebruikt geen service worker; ruim oude registraties (uit
  //    eerdere experimenten of hosting-defaults) op zodat die nooit een
  //    verouderde bundle kunnen blijven serveren.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => registrations.forEach((registration) => registration.unregister()))
      .catch(() => {});
  }

  // 1. Periodieke controle op de achtergrond.
  setInterval(checkForUpdate, CHECK_INTERVAL_MS);

  // 2. App komt terug in beeld (bookmark/PWA geopend of tab weer actief):
  //    direct controleren en meteen herladen — dit is het veilige moment.
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState !== 'visible') return;
    if (await checkForUpdate()) safeReload();
  });

  // 3. Routewissel terwijl er een update klaarstaat: maak er een harde
  //    navigatie van zodat de nieuwe index.html + bundles geladen worden.
  router.beforeEach((to) => {
    if (updateAvailable) {
      window.location.assign(to.fullPath);
      return false;
    }
    return true;
  });

  // 4. Vangnet: een oude index.html verwijst naar chunks die na een deploy
  //    niet meer bestaan. Vite geeft dan dit event — herladen lost het op.
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    safeReload();
  });

  // Eerste controle kort na het opstarten.
  setTimeout(checkForUpdate, 10 * 1000);
}
